import React, { useMemo } from 'react';
import {
  Paper,
  Typography,
  Box,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
} from '@mui/material';
import {
  ExpandMore,
  Edit,
  Schedule,
} from '@mui/icons-material';
import { ScheduleJob } from '../hooks/useScheduleData';
import {
  getTodayInUserTimezone,
  getDayOfWeekInUserTimezone,
  getUTCDateString,
  isToday,
} from '../utils/dateUtils';
import InfoTip from './InfoTip';

interface ThisWeeksScheduleProps {
  scheduledJobs: ScheduleJob[];
  onEditSchedule: (job: ScheduleJob) => void;
  formatServiceTypeName: (name?: string) => string;
}

/**
 * Component for displaying This Week's Schedule
 * Shows jobs organized by day of the week (Monday-Saturday)
 */
const ThisWeeksSchedule: React.FC<ThisWeeksScheduleProps> = ({
  scheduledJobs,
  onEditSchedule,
  formatServiceTypeName,
}) => {
  // PERFORMANCE OPTIMIZATION: Pre-filter and group jobs by day using useMemo
  // This prevents re-filtering all jobs on every render
  const jobsByDay = useMemo(() => {
    // Check if Dan Collins is in the initial array
    const danCollinsJobs = scheduledJobs.filter((j: ScheduleJob) => {
      const customerName = j.customerId ? `${j.customerId.firstName || ''} ${j.customerId.lastName || ''}`.trim() : 'Unknown';
      return customerName.toLowerCase().includes('dan') && customerName.toLowerCase().includes('collins');
    });
    
    // Log all snow removal services in the input array
    const snowRemovalJobs = scheduledJobs.filter((j: ScheduleJob) => j.serviceType?.category === 'snow_removal');
    const thursdaySnowRemoval = snowRemovalJobs.filter((j: ScheduleJob) => {
      if (!j.scheduledDate) return false;
      const scheduledDate = new Date(j.scheduledDate);
      const scheduledYear = scheduledDate.getUTCFullYear();
      const scheduledMonth = scheduledDate.getUTCMonth();
      const scheduledDay = scheduledDate.getUTCDate();
      // Check if scheduled for Jan 15, 2026 (Thursday)
      return scheduledYear === 2026 && scheduledMonth === 0 && scheduledDay === 15;
    });
    
    console.log('[THIS-WEEKS-SCHEDULE] useMemo recalculating - scheduledJobs changed:', {
      jobCount: scheduledJobs.length,
      jobIds: scheduledJobs.map(j => (j._id?.toString() || j.id?.toString())?.slice(-8)),
      scheduledDates: scheduledJobs.map(j => j.scheduledDate),
      snowRemovalJobCount: snowRemovalJobs.length,
      thursdaySnowRemovalCount: thursdaySnowRemoval.length,
      thursdaySnowRemovalJobs: thursdaySnowRemoval.map((j: ScheduleJob) => ({
        jobId: j._id,
        customerName: j.customerId ? `${j.customerId.firstName || ''} ${j.customerId.lastName || ''}`.trim() : 'Unknown',
        scheduledDate: j.scheduledDate,
        completedDate: (j as any).completedDate || 'none',
        status: j.status
      })),
      danCollinsJobsInArray: danCollinsJobs.length,
      danCollinsJobIds: danCollinsJobs.map(j => j._id),
      danCollinsScheduledDates: danCollinsJobs.map(j => j.scheduledDate)
    });
    const renderStartTime = performance.now();
    
    // Calculate week range once
    // Week runs Sunday-Saturday (Sunday is day 0, Saturday is day 6)
    const todayStr = getTodayInUserTimezone();
    const [todayYear, todayMonth, todayDay] = todayStr.split('-').map(Number);
    const todayDayOfWeek = getDayOfWeekInUserTimezone(new Date());
    
    // Calculate days from Sunday (start of week)
    // Sunday = 0, so daysFromSunday = 0
    // Monday = 1, so daysFromSunday = 1
    // etc.
    const daysFromSunday = todayDayOfWeek;
    
    // Start of week is Sunday (today - daysFromSunday)
    // End of week is Saturday (today + (6 - daysFromSunday))
    const startOfWeekUTC = new Date(Date.UTC(todayYear, todayMonth - 1, todayDay - daysFromSunday, 0, 0, 0, 0));
    const endOfWeekUTC = new Date(Date.UTC(todayYear, todayMonth - 1, todayDay + (6 - daysFromSunday), 23, 59, 59, 999));
    
    // Group jobs by day of week (0=Sunday, 1=Monday, etc.)
    const grouped: Record<number, ScheduleJob[]> = {
      1: [], // Monday
      2: [], // Tuesday
      3: [], // Wednesday
      4: [], // Thursday
      5: [], // Friday
      6: [], // Saturday
    };
    
    // Filter and group jobs in a single pass
    scheduledJobs.forEach((job) => {
      const serviceCategory = job.serviceType?.category || 'unknown';
      const isSnowRemoval = serviceCategory === 'snow_removal';
      const customerName = job.customerId ? `${job.customerId.firstName || ''} ${job.customerId.lastName || ''}`.trim() : 'Unknown';
      const completedDate = (job as any).completedDate || (job as any).lastServiceDate;
      
      // Log all snow removal services being processed
      if (isSnowRemoval) {
        console.log('[THIS-WEEKS-SCHEDULE] Processing snow removal service:', {
          jobId: job._id,
          customerName,
          scheduledDate: job.scheduledDate,
          completedDate: completedDate || 'none',
          status: job.status,
          hasCompletedDate: !!completedDate
        });
      }
      
      
      // Check if completedDate is within the current week
      // CRITICAL: For snow removal, we want both completed AND scheduled services to show
      // Logic:
      // 1. If completedDate exists AND completedDate < today AND completedDate is within current week
      //    → ALWAYS use completedDate (service was completed earlier, show under completion day)
      // 2. If completedDate exists AND completedDate = today
      //    → Use completedDate (service completed today, show under today)
      // 3. If no completedDate OR completedDate is outside current week
      //    → Use scheduledDate (new service or old completed service, show under scheduled day)
      // This ensures:
      // - Services completed yesterday show under yesterday (even if rescheduled to today)
      // - New services scheduled for today show under today (they have no completedDate or completedDate is old)
      // - Services completed today show under today
      let useCompletedDate = false;
      const todayUTC = new Date(Date.UTC(todayYear, todayMonth - 1, todayDay, 0, 0, 0, 0));
      
      // Calculate completedDate values outside the if block so they're available for dual-grouping logic
      let completedDateUTC: Date | null = null;
      let completedInThisWeek = false;
      let completedIsToday = false;
      let completedIsBeforeToday = false;
      
      if (completedDate) {
        const completedDateObj = new Date(completedDate);
        const completedYear = completedDateObj.getUTCFullYear();
        const completedMonth = completedDateObj.getUTCMonth();
        const completedDay = completedDateObj.getUTCDate();
        completedDateUTC = new Date(Date.UTC(completedYear, completedMonth, completedDay, 0, 0, 0, 0));
        
        // Check if completedDate is within the current week
        completedInThisWeek = completedDateUTC >= startOfWeekUTC && completedDateUTC <= endOfWeekUTC;
        completedIsToday = completedDateUTC.getTime() === todayUTC.getTime();
        completedIsBeforeToday = completedDateUTC < todayUTC;
        
        if (completedInThisWeek) {
          // CRITICAL: For snow removal, if scheduled for today, ALWAYS use scheduledDate
          // This ensures jobs added via "ADD TO TODAY" show under today, even if they were completed yesterday
          // Snow removal services are unique - they can be scheduled multiple times per week
          // Jobs scheduled for today should show under today regardless of when they were last completed
          if (isSnowRemoval && job.scheduledDate) {
            const scheduledDateObj = new Date(job.scheduledDate);
            const scheduledYear = scheduledDateObj.getUTCFullYear();
            const scheduledMonth = scheduledDateObj.getUTCMonth();
            const scheduledDay = scheduledDateObj.getUTCDate();
            const scheduledDateUTC = new Date(Date.UTC(scheduledYear, scheduledMonth, scheduledDay, 0, 0, 0, 0));
            const scheduledIsToday = scheduledDateUTC.getTime() === todayUTC.getTime();
            
            if (scheduledIsToday) {
              // Snow removal scheduled for today - use scheduledDate to show under today
              useCompletedDate = false;
              console.log('[THIS-WEEKS-SCHEDULE] ✅ Snow removal: Scheduled for today, using scheduledDate (ignore completedDate from yesterday):', {
                jobId: job._id,
                customerName,
                scheduledDate: job.scheduledDate,
                completedDate: completedDate,
                scheduledIsToday,
                completedIsBeforeToday,
                note: 'Snow removal scheduled for today shows under today, even if completed yesterday'
              });
            } else if (completedIsBeforeToday) {
              // Snow removal scheduled for future/other day but completed earlier - use completedDate
              useCompletedDate = true;
              console.log('[THIS-WEEKS-SCHEDULE] Snow removal: Not scheduled for today, using completedDate (completed earlier):', {
                jobId: job._id,
                customerName,
                scheduledDate: job.scheduledDate,
                completedDate: completedDate,
                note: 'Service was completed earlier - will show under completion day'
              });
            }
          } else if (completedIsBeforeToday) {
            // For non-snow-removal, if completedDate is earlier than today, ALWAYS use it
            // This ensures services completed earlier stay under their completion day, even if rescheduled
            useCompletedDate = true;
            if (isSnowRemoval) {
              console.log('[THIS-WEEKS-SCHEDULE] Snow removal: Using completedDate (completed earlier this week):', {
                jobId: job._id,
                customerName,
                completedDate: completedDate,
                scheduledDate: job.scheduledDate,
                note: 'Service was completed earlier - will show under completion day'
              });
            }
          } else if (completedIsToday) {
            // Completed today - BUT for snow removal, always use scheduledDate to avoid timezone issues
            // Snow removal jobs scheduled for today should show under today, even if completedDate exists
            // The completedDate might have timezone interpretation issues that cause it to appear as yesterday
            if (!isSnowRemoval) {
              // For non-snow-removal, use completedDate (both dates are today, either works)
              useCompletedDate = true;
            } else {
              // For snow removal, use scheduledDate to ensure correct day grouping
              useCompletedDate = false;
              console.log('[THIS-WEEKS-SCHEDULE] ✅ Snow removal: Completed today but using scheduledDate for grouping (avoid timezone issues):', {
                jobId: job._id,
                customerName,
                completedDate: completedDate,
                scheduledDate: job.scheduledDate,
                completedDateUTC: completedDateUTC.toISOString(),
                todayUTC: todayUTC.toISOString(),
                completedIsToday,
                note: 'Using scheduledDate to ensure correct day grouping despite completedDate being today'
              });
            }
          } else {
            // completedDate is not before today and not today - must be in the future (shouldn't happen)
            // For snow removal, use scheduledDate
            if (isSnowRemoval) {
              useCompletedDate = false;
              console.log('[THIS-WEEKS-SCHEDULE] ⚠️ Snow removal: completedDate is not before/on today, using scheduledDate:', {
                jobId: job._id,
                customerName,
                completedDate: completedDate,
                scheduledDate: job.scheduledDate,
                completedDateUTC: completedDateUTC.toISOString(),
                todayUTC: todayUTC.toISOString(),
                completedIsBeforeToday,
                completedIsToday,
                completedInThisWeek
              });
            }
          }
          // Note: If completedDate is in the future (shouldn't happen but handle gracefully)
          // we'll fall through to use scheduledDate
        }
      }
      
      // Determine which date to use for grouping
      let dateToUse: Date | null = null;
      if (useCompletedDate) {
        // Use completedDate if it's within the current week (so completed services stay visible under the day they were completed)
        dateToUse = new Date(completedDate);
        console.log('[THIS-WEEKS-SCHEDULE] Using completedDate for grouping (completed this week):', {
          jobId: job._id,
          completedDate: completedDate,
          scheduledDate: job.scheduledDate,
          serviceCategory,
          note: 'Completed service will appear under the day it was completed (within current week) even though scheduledDate has been updated'
        });
      } else if (job.scheduledDate) {
        // Use scheduledDate for non-completed or future services (including new services scheduled for today)
        dateToUse = new Date(job.scheduledDate);
        if (isSnowRemoval) {
          console.log('[THIS-WEEKS-SCHEDULE] 🟢 Snow removal: Using scheduledDate for grouping:', {
            jobId: job._id,
            customerName,
            scheduledDate: job.scheduledDate,
            completedDate: completedDate || 'none',
            status: job.status,
            useCompletedDate,
            note: completedDate ? 'Service has completedDate but using scheduledDate (completedDate outside week or not applicable, or completed today to avoid timezone issues)' : 'New service - no completedDate, using scheduledDate'
          });
        } else {
          // Log for non-snow-removal services too, to debug
          if (!completedDate) {
            console.log('[THIS-WEEKS-SCHEDULE] Service with no completedDate - using scheduledDate:', {
              jobId: job._id,
              customerName,
              scheduledDate: job.scheduledDate,
              status: job.status,
              serviceCategory
            });
          }
        }
      } else {
        // No date available - skip this job
        console.warn('[THIS-WEEKS-SCHEDULE] ⚠️ Service filtered out - no scheduledDate:', {
          jobId: job._id,
          customerName,
          status: job.status,
          completedDate: completedDate || 'none',
          serviceCategory: isSnowRemoval ? 'snow_removal' : serviceCategory
        });
        return;
      }
      
      // CRITICAL: Extract UTC date components directly (dates are stored as UTC midnight)
      const jobYear = dateToUse.getUTCFullYear();
      const jobMonth = dateToUse.getUTCMonth();
      const jobDay = dateToUse.getUTCDate();
      const jobDateUTC = new Date(Date.UTC(jobYear, jobMonth, jobDay, 0, 0, 0, 0));
      
      // Only include jobs within the current week (compare UTC dates)
      if (jobDateUTC < startOfWeekUTC || jobDateUTC > endOfWeekUTC) {
        console.log('[THIS-WEEKS-SCHEDULE] Job filtered out - outside week range:', {
          jobId: job._id,
          jobDateUTC: jobDateUTC.toISOString(),
          startOfWeekUTC: startOfWeekUTC.toISOString(),
          endOfWeekUTC: endOfWeekUTC.toISOString(),
          usingCompletedDate: useCompletedDate
        });
        return;
      }
      
      // Get day of week using UTC date components (consistent with how dates are stored)
      // Create a date at noon UTC to avoid DST issues when determining day of week
      const jobDateAtNoon = new Date(Date.UTC(jobYear, jobMonth, jobDay, 12, 0, 0, 0));
      const jobDayOfWeek = getDayOfWeekInUserTimezone(jobDateAtNoon);
      
      // CRITICAL: Verify day-of-week calculation for debugging
      if (isSnowRemoval || (!completedDate && job.scheduledDate)) {
        const dayNameFromCalc = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'][jobDayOfWeek];
        const actualDateInUserTZ = new Intl.DateTimeFormat('en-US', {
          timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Chicago',
          weekday: 'long',
          year: 'numeric',
          month: 'numeric',
          day: 'numeric'
        }).format(jobDateAtNoon);
        console.log('[THIS-WEEKS-SCHEDULE] Day-of-week calculation check:', {
          jobId: job._id,
          customerName,
          jobDateUTC: jobDateUTC.toISOString(),
          jobYear,
          jobMonth: jobMonth + 1, // 0-indexed to 1-indexed
          jobDay,
          jobDayOfWeek,
          dayNameFromCalc,
          actualDateInUserTZ,
          willGroupToIndex: jobDayOfWeek >= 1 && jobDayOfWeek <= 6 ? jobDayOfWeek : 'FILTERED',
          expectedDayName: jobDayOfWeek === 5 ? 'Friday' : jobDayOfWeek === 4 ? 'Thursday' : 'Other'
        });
      }
      
      // customerName is already declared at the start of the forEach loop (line 93)
      const isDanCollins = customerName.toLowerCase().includes('dan') && customerName.toLowerCase().includes('collins');
      
      if (isDanCollins) {
        console.log('[THIS-WEEKS-SCHEDULE] 🔍 Processing Dan Collins job:', {
          jobId: job._id,
          scheduledDate: job.scheduledDate,
          jobDateUTC: jobDateUTC.toISOString(),
          jobYear,
          jobMonth,
          jobDay,
          jobDayOfWeek,
          dayName: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'][jobDayOfWeek],
          status: job.status,
          customerName,
          willBeGrouped: jobDayOfWeek >= 1 && jobDayOfWeek <= 6
        });
      }
      
      console.log('[THIS-WEEKS-SCHEDULE] Processing job:', {
        jobId: job._id,
        scheduledDate: job.scheduledDate,
        jobDateUTC: jobDateUTC.toISOString(),
        jobDayOfWeek,
        dayName: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'][jobDayOfWeek],
        status: job.status
      });
      
      // CRITICAL: For snow removal jobs with both completedDate (earlier this week) and scheduledDate (today or later),
      // add them to BOTH day groups so they appear under both the completion day AND the scheduled day
      // This ensures that services completed earlier in the week remain visible even when a new service is scheduled/completed today
      const scheduledDateObj = job.scheduledDate ? new Date(job.scheduledDate) : null;
      const scheduledDateUTC = scheduledDateObj ? new Date(Date.UTC(
        scheduledDateObj.getUTCFullYear(),
        scheduledDateObj.getUTCMonth(),
        scheduledDateObj.getUTCDate(),
        0, 0, 0, 0
      )) : null;
      const scheduledIsTodayOrLater = scheduledDateUTC && scheduledDateUTC >= todayUTC;
      
      if (isSnowRemoval && completedDate && job.scheduledDate && completedIsBeforeToday && completedDateUTC && scheduledIsTodayOrLater) {
        // This job was completed earlier this week AND scheduled for today or later
        // Add it to BOTH the completion day AND the scheduled day
        
        // First, add to completion day (using completedDate)
        if (completedInThisWeek) {
          const completedDateObj = new Date(completedDate);
          const completedYear = completedDateObj.getUTCFullYear();
          const completedMonth = completedDateObj.getUTCMonth();
          const completedDay = completedDateObj.getUTCDate();
          const completedDateAtNoon = new Date(Date.UTC(completedYear, completedMonth, completedDay, 12, 0, 0, 0));
          const completedDayOfWeek = getDayOfWeekInUserTimezone(completedDateAtNoon);
          
          if (completedDayOfWeek >= 1 && completedDayOfWeek <= 6) {
            grouped[completedDayOfWeek].push(job);
            console.log('[THIS-WEEKS-SCHEDULE] ✅ Snow removal: Added to completion day:', {
              jobId: job._id,
              customerName,
              completedDayOfWeek,
              completedDate: completedDate,
              scheduledDate: job.scheduledDate,
              note: 'Snow removal job appears under completion day AND scheduled day'
            });
          }
        }
        
        // Then, add to scheduled day (use scheduledDate's day-of-week, NOT jobDayOfWeek which is from completedDate)
        const sy = scheduledDateUTC!.getUTCFullYear();
        const sm = scheduledDateUTC!.getUTCMonth();
        const sd = scheduledDateUTC!.getUTCDate();
        const scheduledDateAtNoon = new Date(Date.UTC(sy, sm, sd, 12, 0, 0, 0));
        const scheduledDayOfWeek = getDayOfWeekInUserTimezone(scheduledDateAtNoon);
        const scheduledInWeek = scheduledDateUTC! >= startOfWeekUTC && scheduledDateUTC! <= endOfWeekUTC;
        if (scheduledInWeek && scheduledDayOfWeek >= 1 && scheduledDayOfWeek <= 6) {
          grouped[scheduledDayOfWeek].push(job);
          console.log('[THIS-WEEKS-SCHEDULE] ✅ Snow removal: Added to scheduled day:', {
            jobId: job._id,
            customerName,
            scheduledDayOfWeek,
            scheduledDate: job.scheduledDate,
            completedDate: completedDate,
            note: 'Snow removal job appears under completion day AND scheduled day'
          });
        }
      } else {
        // Normal case: Add to single day group
        if (jobDayOfWeek >= 1 && jobDayOfWeek <= 6) {
          grouped[jobDayOfWeek].push(job);
          // Log all jobs going into Friday (5) or Thursday (4) to debug the mismatch
          if (jobDayOfWeek === 4 || jobDayOfWeek === 5) {
            console.log('[THIS-WEEKS-SCHEDULE] ✅ Job added to grouped array:', {
              jobId: job._id,
              customerName,
              jobDayOfWeek,
              dayName: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'][jobDayOfWeek],
              arrayIndex: jobDayOfWeek,
              expectedDayLabel: jobDayOfWeek === 4 ? 'Thursday' : 'Friday',
              groupedCount: grouped[jobDayOfWeek].length,
              scheduledDate: job.scheduledDate,
              completedDate: completedDate || 'none'
            });
          }
          if (isDanCollins) {
            console.log('[THIS-WEEKS-SCHEDULE] ✅ Dan Collins job added to day', jobDayOfWeek, ':', {
              jobId: job._id,
              dayName: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'][jobDayOfWeek],
              groupedCount: grouped[jobDayOfWeek].length
            });
          }
        } else {
          console.log('[THIS-WEEKS-SCHEDULE] Job filtered out - not Monday-Saturday:', {
            jobId: job._id,
            jobDayOfWeek,
            isDanCollins: isDanCollins || undefined
          });
        }
      }
    });
    
    // Deduplicate jobs per day (same customer + service type)
    // CRITICAL: Snow removal services can have multiple instances per week/day, so we include job._id in unique key
    // Mowing and fertilizing services should still deduplicate (one per day per customer + property)
    const days = [1, 2, 3, 4, 5, 6];
    days.forEach(dayOfWeek => {
      const customerServiceJobMap = new Map<string, ScheduleJob>();
      const beforeDedupCount = grouped[dayOfWeek].length;
      
      grouped[dayOfWeek].forEach((job: ScheduleJob) => {
        const customerId = job.customerId?._id?.toString() || job.customerId?.toString() || job.customerId;
        if (!customerId) return;
        
        const customerName = job.customerId ? `${job.customerId.firstName || ''} ${job.customerId.lastName || ''}`.trim() : 'Unknown';
        const isDanCollins = customerName.toLowerCase().includes('dan') && customerName.toLowerCase().includes('collins');
        
        const serviceTypeId = job.serviceType?._id?.toString() || 
                             job.serviceType?.id?.toString() || 
                             job.serviceType?.category || 
                             'unknown';
        const serviceCategory = job.serviceType?.category || 'unknown';
        const isSnowRemoval = serviceCategory === 'snow_removal';
        
        // Include address in unique key to allow same customer + service at different properties
        // Same customer CAN have same service at different properties on same day
        // Same customer CANNOT have same service at same property on same day (except snow removal)
        const addressKey = job.address?.street && job.address?.zipCode
          ? `${job.address.street}-${job.address.zipCode}`
          : job.address?.street || job._id?.toString() || 'no-address';
        
        // For snow removal: Include job._id AND completedDate in unique key to allow multiple instances per day/week
        // This ensures that a job completed yesterday and a job scheduled/completed today are treated as separate entries
        // For mowing/fertilizing: Use customer-service-address to prevent duplicates (one per day per property)
        const jobId = job._id?.toString() || job.id?.toString() || '';
        const completedDate = (job as any).completedDate;
        const completedDateStr = completedDate ? new Date(completedDate).toISOString().split('T')[0] : 'no-completed';
        const uniqueKey = isSnowRemoval
          ? `${customerId}-${serviceTypeId}-${addressKey}-${jobId}-${completedDateStr}` // Each snow removal instance is unique (by jobId AND completedDate)
          : `${customerId}-${serviceTypeId}-${addressKey}`; // Mowing/fertilizing: one per day per property
        
        const existingJob = customerServiceJobMap.get(uniqueKey);
        if (!existingJob) {
          customerServiceJobMap.set(uniqueKey, job);
          if (isDanCollins && dayOfWeek === 5) {
            console.log('[THIS-WEEKS-SCHEDULE] ✅ Dan Collins job added to deduplication map (Friday):', {
              jobId: job._id,
              uniqueKey,
              scheduledDate: job.scheduledDate
            });
          }
        } else {
          // SINGLE SOURCE OF TRUTH: Prefer jobs with completedDate within current week
          // This ensures completed services remain visible even after auto-rescheduling
          const existingCompletedDate = (existingJob as any).completedDate;
          const currentCompletedDate = (job as any).completedDate;
          
          // Check if completedDate is within current week
          const existingHasCompletedThisWeek = existingCompletedDate && 
            new Date(existingCompletedDate) >= startOfWeekUTC && 
            new Date(existingCompletedDate) <= endOfWeekUTC;
          const currentHasCompletedThisWeek = currentCompletedDate && 
            new Date(currentCompletedDate) >= startOfWeekUTC && 
            new Date(currentCompletedDate) <= endOfWeekUTC;
          
          // Prefer job with completedDate within current week
          if (currentHasCompletedThisWeek && !existingHasCompletedThisWeek) {
            customerServiceJobMap.set(uniqueKey, job);
            return; // Skip to next iteration in forEach
          }
          if (existingHasCompletedThisWeek && !currentHasCompletedThisWeek) {
            // Keep existing job
            return; // Skip to next iteration in forEach
          }
          
          // If both or neither have completedDate, compare scheduledDate
          const existingDate = existingJob.scheduledDate ? new Date(existingJob.scheduledDate) : null;
          const currentDate = job.scheduledDate ? new Date(job.scheduledDate) : null;
          
          if (isDanCollins && dayOfWeek === 5) {
            console.log('[THIS-WEEKS-SCHEDULE] ⚠️ Dan Collins job duplicate found (Friday):', {
              jobId: job._id,
              existingJobId: existingJob._id,
              currentDate: currentDate?.toISOString(),
              existingDate: existingDate?.toISOString(),
              currentCompletedDate: currentCompletedDate?.toISOString(),
              existingCompletedDate: existingCompletedDate?.toISOString(),
              willReplace: currentDate && existingDate ? currentDate >= existingDate : (currentDate && !existingDate)
            });
          }
          
          if (currentDate && existingDate) {
            if (currentDate >= existingDate) {
              customerServiceJobMap.set(uniqueKey, job);
              if (isDanCollins && dayOfWeek === 5) {
                console.log('[THIS-WEEKS-SCHEDULE] ✅ Dan Collins job replaced existing (newer date):', {
                  jobId: job._id,
                  scheduledDate: job.scheduledDate
                });
              }
            } else {
              if (isDanCollins && dayOfWeek === 5) {
                console.log('[THIS-WEEKS-SCHEDULE] ❌ Dan Collins job NOT replaced (older date):', {
                  jobId: job._id,
                  scheduledDate: job.scheduledDate,
                  existingJobId: existingJob._id,
                  existingScheduledDate: existingJob.scheduledDate
                });
              }
            }
          } else if (currentDate && !existingDate) {
            customerServiceJobMap.set(uniqueKey, job);
            if (isDanCollins && dayOfWeek === 5) {
              console.log('[THIS-WEEKS-SCHEDULE] ✅ Dan Collins job replaced existing (existing has no date):', {
                jobId: job._id,
                scheduledDate: job.scheduledDate
              });
            }
          } else {
            if (isDanCollins && dayOfWeek === 5) {
              console.log('[THIS-WEEKS-SCHEDULE] ❌ Dan Collins job NOT replaced (no current date):', {
                jobId: job._id,
                scheduledDate: job.scheduledDate
              });
            }
          }
        }
      });
      
      const afterDedupCount = customerServiceJobMap.size;
      if (dayOfWeek === 5 && beforeDedupCount !== afterDedupCount) {
        console.log('[THIS-WEEKS-SCHEDULE] ⚠️ Friday deduplication removed jobs:', {
          before: beforeDedupCount,
          after: afterDedupCount,
          removed: beforeDedupCount - afterDedupCount
        });
      }
      
      grouped[dayOfWeek] = Array.from(customerServiceJobMap.values());
      
      // Log final Friday jobs
      if (dayOfWeek === 5) {
        const fridayJobs = grouped[dayOfWeek];
        const danCollinsJobs = fridayJobs.filter((j: ScheduleJob) => {
          const customerName = j.customerId ? `${j.customerId.firstName || ''} ${j.customerId.lastName || ''}`.trim() : 'Unknown';
          return customerName.toLowerCase().includes('dan') && customerName.toLowerCase().includes('collins');
        });
        console.log('[THIS-WEEKS-SCHEDULE] 📋 Final Friday jobs after deduplication:', {
          totalJobs: fridayJobs.length,
          danCollinsJobs: danCollinsJobs.length,
          danCollinsJobIds: danCollinsJobs.map((j: ScheduleJob) => j._id)
        });
      }
    });
    
    const renderTime = performance.now() - renderStartTime;
    console.log(`[THIS-WEEKS-SCHEDULE] Pre-filtered ${scheduledJobs.length} jobs into days in ${renderTime.toFixed(2)}ms`);
    
    // CRITICAL DEBUG: Log what's in each grouped array to diagnose the display issue
    console.log('[THIS-WEEKS-SCHEDULE] 📊 Final grouped arrays summary:', {
      'Monday (1)': grouped[1].length,
      'Tuesday (2)': grouped[2].length,
      'Wednesday (3)': grouped[3].length,
      'Thursday (4)': grouped[4].length,
      'Friday (5)': grouped[5].length,
      'Saturday (6)': grouped[6].length,
      'Thursday jobs (index 4)': grouped[4].map((j: ScheduleJob) => ({
        jobId: j._id,
        customerName: j.customerId ? `${j.customerId.firstName || ''} ${j.customerId.lastName || ''}`.trim() : 'Unknown',
        scheduledDate: j.scheduledDate,
        serviceType: j.serviceType?.name || j.serviceType?.category
      })),
      'Friday jobs (index 5)': grouped[5].map((j: ScheduleJob) => ({
        jobId: j._id,
        customerName: j.customerId ? `${j.customerId.firstName || ''} ${j.customerId.lastName || ''}`.trim() : 'Unknown',
        scheduledDate: j.scheduledDate,
        serviceType: j.serviceType?.name || j.serviceType?.category
      }))
    });
    
    return grouped;
  }, [scheduledJobs]);

  return (
    <Paper sx={{ p: 2 }}>
      <Typography variant="h6" gutterBottom sx={{ display: 'inline-flex', alignItems: 'center' }}>
        This Week's Schedule
        <InfoTip title="What’s already on each weekday from recurring and scheduled services. Use the pencil to change a date." />
      </Typography>
      {scheduledJobs.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
          No jobs scheduled this week
        </Typography>
      ) : (
        <Box>
          {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'].map((day, index) => {
            const dayOfWeek = index + 1; // Monday=1, Tuesday=2, etc.
            const dayJobs = jobsByDay[dayOfWeek] || [];

            return (
              <Accordion key={day}>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Typography variant="subtitle1">
                    {day} ({dayJobs.length} jobs)
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  {dayJobs.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      No jobs scheduled
                    </Typography>
                  ) : (
                    <List dense>
                      {dayJobs.map((job) => (
                        <ListItem key={job._id} divider>
                          <ListItemText
                            primary={
                              <Typography variant="body2">
                                {job.customerId?.lastName}, {job.customerId?.firstName} - {formatServiceTypeName(job.serviceType?.name)}
                              </Typography>
                            }
                            secondary={
                              <Typography variant="caption" color="text.secondary">
                                {job.address?.street}, {job.address?.city}
                              </Typography>
                            }
                          />
                          <ListItemSecondaryAction>
                            <IconButton
                              edge="end"
                              onClick={() => onEditSchedule(job)}
                              title="Edit schedule: change next date or recurring pattern"
                              size="small"
                            >
                              <Edit />
                            </IconButton>
                          </ListItemSecondaryAction>
                        </ListItem>
                      ))}
                    </List>
                  )}
                </AccordionDetails>
              </Accordion>
            );
          })}
          
          {/* Unscheduled Jobs Section */}
          {(() => {
            const unscheduledJobs = scheduledJobs.filter(job => !job.scheduledDate);
            return unscheduledJobs.length > 0 && (
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Typography variant="subtitle1" color="warning.main">
                    Needs Scheduling ({unscheduledJobs.length} jobs)
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <List dense>
                    {unscheduledJobs.map((job) => (
                    <ListItem key={job._id} divider>
                      <ListItemText
                        primary={
                          <Typography variant="body2">
                            Needs Scheduling - {formatServiceTypeName(job.serviceType?.name)}
                          </Typography>
                        }
                        secondary={
                          <Typography variant="caption" color="text.secondary">
                            {job.address?.street}, {job.address?.city}
                          </Typography>
                        }
                      />
                      <ListItemSecondaryAction>
                        <IconButton
                          edge="end"
                          onClick={() => onEditSchedule(job)}
                          title="Schedule Service"
                          size="small"
                        >
                          <Schedule />
                        </IconButton>
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>
              </AccordionDetails>
            </Accordion>
          );
          })()}
        </Box>
      )}
    </Paper>
  );
};

export default ThisWeeksSchedule;

